import { createSelector } from '@ngrx/store';

import { BusinessOperatingSystem, UserModel, RoleCode, RoleHelpers } from '@ninety/mobile/platform/api';
import { SegmentContactTraits } from '@ninety/mobile/platform/services';
import { SegmentGroupTraits, SegmentReservedGroupTraits } from '@ninety/mobile/platform/services';

import { CompanyUserListModel } from '../../../app-entities/company-users/company-users-state.model';
import { CompanyUserSelectors } from '../../../app-entities/company-users/company-users-state.selectors';
import { CurrentUserSelectors } from '../../../app-entities/user-list/user-list-state.selectors';
import { CurrentPersonSelectors } from '../../current-person/current-person-state.selectors';
import { selectGainsightUserAttributes } from '../gainsight/gainsight.selectors';
import { IGainsightGlobalContext, NinetyPlatform } from '../models/gainsight-global-context';

export namespace SegmentSelectors {
  export const selectSegmentContactTraits = createSelector(
    CurrentPersonSelectors.person,
    CompanyUserSelectors.selectCurrentCompanyUser,
    (cp, cu) => {
      return {
        id: cp?._id,
        email: cp?.primaryEmail ?? undefined,
        phone: cp?.primaryPhoneNumber ?? undefined,
        firstName: cp?.metadata?.name?.first,
        lastName: cp?.metadata?.name?.last,
        company: {
          id: cu?.companyId,
          name: cu?.companyName,
        },
        bos: cu?.bos,
        userId: cu?.userId,
        lastAccessed: new Date().toISOString(),
      } as SegmentContactTraits | undefined;
    }
  );

  export const selectGroupTraits = createSelector(
    [CompanyUserSelectors.selectCurrentCompanyUser, selectGainsightUserAttributes],
    (cu: CompanyUserListModel | null, gua) =>
      ({
        gainsight: gua,
        affiliateCode: cu?.company?.affiliateCode,
        clearbit_employees_range: cu?.company?.employeeRange,
        clearbit_industry: cu?.company?.industry,
        implementerCode: cu?.company?.implementerCode,
        implementerFree: cu?.company?.implementerFree,
        parentAffiliateCode: cu?.company?.parentAffiliateCode,
        partnerKey: cu?.company?.partnerKey,
        trialEndDate: cu?.company?.trialingUntil,
        accountStatus: cu?.company?.accountStatus,
        stripeStatus: cu?.company?.subscription?.providerStatus,
        subscriptionProvider: cu?.company?.subscription?.provider,
        bos: cu?.bos,
      }) as SegmentReservedGroupTraits | SegmentGroupTraits | undefined
  );

  export const selectGainsightContext = createSelector(
    selectSegmentContactTraits,
    CompanyUserSelectors.selectCurrentCompanySltTeamId,
    CurrentUserSelectors.selectUser,
    (contact, sltTeamId, ccu) => {
      const safeContact = contact ?? ({} as SegmentContactTraits);
      const safeSltTeamId = sltTeamId ?? '';
      const safeTeamIds = ccu?.teams ? ccu.teams.map(t => t.teamId) : [];
      const safeBos = safeContact?.bos ?? ({} as BusinessOperatingSystem);

      const gainsightContext = ccu
        ? getCommonTraits(ccu, safeBos, safeSltTeamId, safeTeamIds)
        : getCommonTraits({} as UserModel, {} as BusinessOperatingSystem, '', []);
      return gainsightContext;
    }
  );
}

function getCommonTraits(
  cu: UserModel,
  bos: BusinessOperatingSystem,
  sltTeamId: string,
  teams: string[]
): IGainsightGlobalContext {
  return {
    currentUserId: cu?._id ?? '',
    currentUserIsImplementer: cu?.isImplementer ?? false,
    currentUserRoleCode: cu?.roleCode ?? RoleCode.observer,
    currentUserRoleName: RoleHelpers.roleName(cu?.roleCode ?? RoleCode.observer),
    currentPersonId: cu?.personId ?? '',
    currentUserCompany: cu?.company?._id ?? '',
    currentUserCompanyBos: bos,
    url: location.href,
    urlPath: location.pathname,
    isLeadership: teams.some((t: string) => t === sltTeamId) ?? false,
    ninetyPlatform: NinetyPlatform.Mobile,
  } as const;
}
